import type React from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface DropdownProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  /**
   * Render into a portal at document.body, positioned with `fixed` coordinates
   * computed from the trigger's on-screen position, instead of `absolute`
   * inside the trigger's own stacking context. Use this when the trigger sits
   * inside a scrollable/clipping ancestor (e.g. a table's `overflow-x-auto`
   * wrapper) — a plain `absolute` dropdown gets clipped there and forces a
   * scrollbar instead of floating above the content.
   */
  usePortal?: boolean;
}

export const Dropdown: React.FC<DropdownProps> = ({
  isOpen,
  onClose,
  children,
  className = "",
  usePortal = false,
}) => {
  const anchorRef = useRef<HTMLSpanElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; right: number } | null>(null);

  useLayoutEffect(() => {
    if (!usePortal || !isOpen || !anchorRef.current) return;

    const updatePosition = () => {
      const rect = anchorRef.current!.getBoundingClientRect();
      setPosition({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [usePortal, isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target.closest(".dropdown-toggle")) return;
      if (menuRef.current && !menuRef.current.contains(target)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  if (usePortal) {
    if (!isOpen) return <span ref={anchorRef} className="pointer-events-none absolute inset-0" aria-hidden="true" />;

    return (
      <>
        <span ref={anchorRef} className="pointer-events-none absolute inset-0" aria-hidden="true" />
        {createPortal(
          <div
            ref={menuRef}
            style={
              position
                ? { position: "fixed", top: position.top, right: position.right }
                : { position: "fixed", visibility: "hidden" }
            }
            className={`z-[9999] rounded-xl border border-gray-200 bg-white shadow-theme-lg dark:border-gray-800 dark:bg-gray-dark ${className}`}
          >
            {children}
          </div>,
          document.body
        )}
      </>
    );
  }

  if (!isOpen) return null;

  return (
    <div
      ref={menuRef}
      className={`absolute z-40  right-0 mt-2  rounded-xl border border-gray-200 bg-white  shadow-theme-lg dark:border-gray-800 dark:bg-gray-dark ${className}`}
    >
      {children}
    </div>
  );
};
